import {BatteryChargeValue, FuelLevelValue, Parameter} from 'platform/components';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {isNotNil} from 'ramda';

import {FullVehicleResponseBody} from '@dms/api/shared';
import {
  GetMetadaServiceCaseApiResponse,
  ServiceCaseDetailBootstrapCheckIn,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  levelIndicatorsFor,
  useGetVehicleHeaderV2,
  usePermissions,
  useVehicleCatalogue,
} from '@dms/shared';

import {Nullish, buildArray, parseDate, composePath, useNavigate} from 'shared/web';

import {formatFuelTank} from '../../../utils/formatFuelTank';

/** The check-in stores the fuel tank level as a 0-1 ratio, not as a percent. */
const MAX_FUEL_TANK_RATIO = 1;

// eslint-disable-next-line complexity -- Pre-existing violation
export function useServiceCaseVehicleHeaderParameters(
  serviceCase: GetMetadaServiceCaseApiResponse | Nullish,
  vehicle: FullVehicleResponseBody | Nullish,
  checkin: ServiceCaseDetailBootstrapCheckIn | Nullish,
  hasComments: boolean,
  additionalNote: string | Nullish
) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const [vehicleUtils, {isLoading: isVehicleCatalogueLoading}] = useVehicleCatalogue(vehicle?.type);
  const [canReadServiceCase] = usePermissions({
    permissionKeys: ['serviceCaseRead'],
    scopes: {
      serviceCaseRead: {
        // BE Handles SC ACL
        branchId: 'SKIP',
        // BE Handles SC ACL
        serviceCaseType: 'SKIP',
      },
    },
  });

  const navigateToInsights = () => {
    navigate(composePath(vehiclesRoutes.insights, {params: {id: vehicle?.id ?? ''}}));
  };

  const formatMileage = (mileage?: number | null) => {
    const formatedMileage = formatNumber(mileage);

    if (!formatedMileage) {
      return EMPTY_PLACEHOLDER;
    }

    return `${formatedMileage} ${i18n.t('general.metric.km')}`;
  };

  const newVehicleHeader = useGetVehicleHeaderV2({
    serviceCaseNumber: serviceCase?.number,
    publicId: vehicle?.publicId,
    vin: vehicle?.vehicle.vin,
    registrationPlate: vehicle?.vehicle.registrationPlate,
    firstRegistrationOn: vehicle?.vehicle.firstRegistrationOn,
    warrantyDate: vehicle?.vehicle.warrantyDate,
    isNotInService: checkin?.isVehicleNotInService,
    isVehicleDetail: false,
    mileage: formatMileage(checkin?.mileage),
    levelIndicators: levelIndicatorsFor(vehicle?.fuelType),
    fuelTank: formatFuelTank(checkin?.fuelTank),
    fuelTankContent: (
      <FuelLevelValue value={checkin?.fuelTank} max={MAX_FUEL_TANK_RATIO} tone="neutral" />
    ),
    // Already a whole percent, unlike the fuel tank's 0-1 ratio.
    batteryCharge: isNotNil(checkin?.batteryCharge) ? `${checkin.batteryCharge} %` : undefined,
    batteryChargeContent: <BatteryChargeValue value={checkin?.batteryCharge} tone="neutral" />,
    fuelType: isVehicleCatalogueLoading ? undefined : vehicleUtils.getFuelType(vehicle?.fuelType),
    engineCode: vehicle?.engine?.engineCode,
    technicalInspectionValidUntil: vehicle?.state?.technicalInspectionValidUntil,
    hasComments,
    additionalNote,
    onIconClick: navigateToInsights,
  });

  if (canReadServiceCase) {
    return newVehicleHeader;
  }

  return buildArray<Parameter>()
    .add(serviceCase?.number)
    .when(vehicle?.vin, vehicle?.vin)
    .when(vehicle?.vehicle.registrationPlate, vehicle?.vehicle.registrationPlate)
    .when(vehicle?.vehicle.firstRegistrationOn, () =>
      formatDateTime('dateShort', parseDate(vehicle?.vehicle.firstRegistrationOn ?? ''))
    )
    .when(vehicle?.modelSpecification?.warrantyDate, () =>
      formatDateTime('dateShort', parseDate(vehicle?.modelSpecification?.warrantyDate ?? ''))
    )
    .when(
      vehicle?.modelSpecification?.warrantyMileage,
      formatNumber(vehicle?.modelSpecification?.warrantyMileage ?? 0)
    );
}
